import { Component, ElementRef, inject, OnDestroy, output, viewChild } from '@angular/core';
import { FileService } from '../../../../services/file/file.service';
import { MatIcon } from '@angular/material/icon';
import { MatIconButton } from '@angular/material/button';
import { SnackbarService } from '../../../../services/snackbar/snackbar.service';
import { TiptapEditorTestIds } from '../tiptap-editor.test-ids';
import { byteToMBString } from '../../../../services/util';

export interface PendingAttachment {
  id: string;
  file: File;
  previewSrc?: string;
  kind: 'image' | 'file';
  name: string;
  mime: string;
  size: number;
}

@Component({
  selector: 'app-file-upload',
  standalone: true,
  imports: [MatIcon, MatIconButton],
  templateUrl: './file-upload.html'
})
export class FileUploadComponent implements OnDestroy {
  readonly attachmentSelected = output<PendingAttachment>();

  readonly fileInput = viewChild<ElementRef<HTMLInputElement>>('fileInput');

  private readonly fileService = inject(FileService);
  private readonly snackbarService = inject(SnackbarService);

  private pendingAttachments: PendingAttachment[] = [];

  ngOnDestroy(): void {
    this.cleanupAttachments();
  }

  async onFileSelected(event: Event): Promise<void> {
    const input = event.target as HTMLInputElement;
    const files = input.files ? Array.from(input.files) : [];

    if (files.length === 0) {
      return;
    }

    try {
      for (const file of files) {
        if (!(await this.fileService.checkUploadSize(file))) {
          const maxFileSize = await this.fileService.getMaxSize();
          const maxUploadMB = byteToMBString(maxFileSize);
          const selectedFileSizeMB = byteToMBString(file.size);
          this.snackbarService.error(
            `Die Datei ist zu groß. Erlaubt sind max. ${maxUploadMB} MB. Deine Datei hat ${selectedFileSizeMB} MB.`
          );
          continue;
        }

        const isValid = await this.fileService.validateFileType(file);

        if (!isValid) {
          this.snackbarService.error(
            'Die ausgewählte Datei konnte nicht geladen werden. Dateityp wird nicht akzeptiert.'
          );
          continue;
        }

        const attachment: PendingAttachment = {
          id: crypto.randomUUID(),
          file,
          kind: file.type.startsWith('image/') ? 'image' : 'file',
          name: file.name,
          mime: file.type || 'application/octet-stream',
          size: file.size,
          previewSrc: file.type.startsWith('image/') ? URL.createObjectURL(file) : undefined
        };

        this.pendingAttachments.push(attachment);
        this.attachmentSelected.emit(attachment);
      }
    } catch (error) {
      console.error('Fehler beim Auswählen der Datei:', error);
    } finally {
      input.value = '';
    }
  }

  reset(): void {
    this.cleanupAttachments();
  }

  openFileDialog(event?: Event): void {
    event?.stopPropagation();
    this.fileInput()?.nativeElement.click();
  }

  private cleanupAttachments(): void {
    for (const attachment of this.pendingAttachments) {
      if (attachment.previewSrc?.startsWith('blob:')) {
        URL.revokeObjectURL(attachment.previewSrc);
      }
    }

    this.pendingAttachments = [];
  }

  protected readonly TiptapEditorTestIds = TiptapEditorTestIds;
}
